import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Clock } from 'lucide-react';
import api from '../../services/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState(sessionId ? 'polling' : 'init');
  const [attempts, setAttempts] = useState(0);
  const polling = useRef(false);
  const maxAttempts = 8;

  useEffect(() => {
    if (!sessionId || polling.current) return;
    polling.current = true;

    const pollStatus = async (attempt) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await api.get(`/payment/checkout/status/${sessionId}`);
        const data = res.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setAttempts(attempt + 1);
        setTimeout(() => pollStatus(attempt + 1), 2500);
      } catch (err) {
        if (attempt < maxAttempts - 1) {
          setTimeout(() => pollStatus(attempt + 1), 2500);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus(0);
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-xl p-8 border border-gray-200 max-w-md w-full text-center shadow-sm">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{"Erreur"}</h2>
          <p className="text-sm text-gray-500 mb-6">{"Session de paiement introuvable."}</p>
          <Link
            to="/payment/product"
            className="inline-flex items-center justify-center bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors w-full"
          >
            {"Retour"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="bg-white rounded-xl p-8 border border-gray-200 max-w-md w-full text-center shadow-sm">
        {(status === 'init' || status === 'polling') && (
          <>
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{"Vérification du paiement..."}</h2>
            <p className="text-sm text-gray-500">{"Veuillez patienter pendant que nous confirmons votre paiement."}</p>
            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-400">{`Tentative ${attempts + 1}/${maxAttempts}`}</p>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{"Paiement confirmé !"}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {"Merci pour votre achat. Votre formation « Book Formation E-Commerce » est maintenant accessible. Votre abonnement de 12 mois est activé."}
            </p>
            <Link
              to="/client"
              className="inline-flex items-center justify-center bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors w-full"
            >
              {"Accéder à mon espace"}
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{"Session expirée"}</h2>
            <p className="text-sm text-gray-500 mb-6">{"La session de paiement a expiré. Veuillez réessayer."}</p>
            <Link
              to="/payment/product"
              className="inline-flex items-center justify-center bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors w-full"
            >
              {"Réessayer"}
            </Link>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{"Erreur de vérification"}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {status === 'timeout'
                ? "La vérification a pris trop de temps. Si vous avez été débité, votre accès sera activé automatiquement."
                : "Une erreur est survenue lors de la vérification du paiement."}
            </p>
            <Link
              to="/payment/product"
              className="inline-flex items-center justify-center bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors w-full"
            >
              {"Retour"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
